import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { useMemo, useState, useEffect } from "react";
import AddUserIcon from "../../Assests/Icons/AddUserIcon";
import { useNavigate } from "react-router-dom";
import Button from "../../Components/Button";
import Loader from "../../Components/Loader";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { useDigitalAssetDefinition } from "../../API/Queries/useDigitalAssetDefinition";

export const RecordsPerPage = 10;
export const SearchLimit = 1000;

const DigitalAssetDefinition = () => {
  const [pageNumber, setPageNumber] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  console.log('searchValue',searchValue);
  
  const [allAssets, setAllAssets] = useState([]);
  const [paginatedAssets, setPaginatedAssets] = useState([]);

  const navigate = useNavigate();
  
  // Create proper filter structure for search
  const searchFilters = searchValue 
    ? [{ field: "name", operator: "contains", value: searchValue }]
    : [];

  // Use the useDigitalAssetDefinition hook to fetch digital asset definitions
  const { data: digitalAssetDefinitions, isLoading: isDigitalAssetLoading, refetch } = useDigitalAssetDefinition(
    { page: pageNumber, limit: RecordsPerPage, filters: searchFilters }
  );

  useEffect(() => {
    console.log("Digital Asset Definitions:", digitalAssetDefinitions);
  }, [digitalAssetDefinitions]);

  const onSearch = () => {
    console.log('searchValue', searchValue);
    setPageNumber(0);
    // The refetch will automatically use the updated searchFilters due to the dependency
    refetch();
  };

  const onClearSearch = () => {
    setSearchValue("");
    setPageNumber(0);
    // The refetch will automatically use empty filters due to the dependency
    refetch();
  };

  const onAddUser = () => navigate("/dam/create-digital-asset-definition");
  const onNext = () => setPageNumber((prev) => prev + 1);
  const onPrevious = () => setPageNumber((prev) => Math.max(0, prev - 1));

  // Update allAssets when data changes
  useEffect(() => {
    if (digitalAssetDefinitions) {
      setAllAssets(digitalAssetDefinitions);
    }
  }, [digitalAssetDefinitions]);

  // Filter assets based on search value
  const filteredAssets = useMemo(() => {
    if (!searchValue.trim()) {
      return allAssets;
    }
    return allAssets.filter(asset => 
      asset.name && asset.name.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [allAssets, searchValue]);

  // Reset to page 0 when search value changes
  useEffect(() => {
    setPageNumber(0);
  }, [searchValue]);

  // Update paginated assets when filteredAssets or pageNumber changes
  useEffect(() => {
    const start = pageNumber * RecordsPerPage;
    const end = start + RecordsPerPage;
    setPaginatedAssets(filteredAssets.slice(start, end));
  }, [filteredAssets, pageNumber]);

  const columnDefs = useMemo(
    () => [
      { field: "id", headerName: "Digital Asset Definition Id", width: 400 },
      { field: "name", headerName: "Digital Asset Definition Name", width: 300 },
      {
        field: "actions",
        headerName: "Actions",
        width: 500,
        cellRenderer: (params) =>
          Array.isArray(params.data.actions)
            ? params.data.actions
                .map((action) => action.name)
                .join(", ")
            : "N/A",
      },
      {
        field: "metadata",
        headerName: "Metadata Fields",
        width: 300,
        cellRenderer: (params) =>
          Array.isArray(params.data.metadata)
            ? params.data.metadata.length
            : "0",
      },
      {
        field: "view",
        headerName: "View Digital Asset Definition",
        width: 300,
        cellRenderer: (params) => (
          <div className="flex items-center h-full">
            <MdOutlineRemoveRedEye
              onClick={() => {
                navigate("/dam/edit-digital-asset-definition", {
                  state: { prefillData: params.data },
                });
              }}
              style={{
                color: "#47B7CF",
                fontSize: "large",
                cursor: "pointer",
              }}
            />
          </div>
        ),
      },
    ],
    [navigate]
  );

  const defaultColDef = useMemo(() => ({ filter: true, sortable: true }), []);

  return (
    <>
      {isDigitalAssetLoading && <Loader />}
      <p className="flex items-center justify-center mt-16 text-lg text-[#0e76a8]">
        <b>Digital Asset Definition</b>
      </p>

      <div className="flex flex-wrap justify-between items-center mb-4 px-4">
        <div className="flex items-center space-x-2">
          <input
            type="text"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Search by Name"
            className="border border-gray-300 rounded px-3 py-2"
          />
          <Button onClick={onSearch}>Search</Button>
          <Button onClick={onClearSearch}>Clear</Button>
          {searchValue && (
            <span className="text-sm text-gray-600">
              {filteredAssets.length} result(s) found
            </span>
          )}
        </div>
        <Button className="flex items-center mt-2 sm:mt-0" onClick={onAddUser}>
          <AddUserIcon className="mr-1" />
          Create new definition
        </Button>
      </div>

      <div
        className="ag-theme-alpine"
        style={{ height: "calc(100vh - 260px)" }}
      >
        <AgGridReact
          rowData={paginatedAssets}
          columnDefs={columnDefs}
          animateRows
          suppressPaginationPanel
          overlayNoRowsTemplate={
            `<span style="padding: 10px; border: 1px solid #ccc; background: #f9f9f9; border-radius: 4px;">` +
            (!isDigitalAssetLoading ? "No Data Available" : "Loading...") +
            `</span>`
          }
          suppressScrollOnNewData
          defaultColDef={defaultColDef}
        />

        <div className="mt-6 flex items-center justify-center">
          <Button disabled={pageNumber === 0 || isDigitalAssetLoading} onClick={onPrevious}>
            Previous
          </Button>
          <p className="mx-2">Page: {pageNumber + 1}</p>
          <Button
            disabled={
              (pageNumber + 1) * RecordsPerPage >= filteredAssets.length || isDigitalAssetLoading
            }
            onClick={onNext}
          >
            Next
          </Button>
        </div>
      </div>
    </>
  );
};

export default DigitalAssetDefinition;